import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { SceneSummary } from '@/hooks/types';

interface RoomHeaderProps {
  name: string;
  scene: SceneSummary | null;
  onStartScene: () => void;
  onEndScene: () => void;
  isStartPending: boolean;
  isEndPending: boolean;
  /** Show the "Edit" affordance — true only when the viewer owns this room (#1470). */
  canEdit?: boolean;
  onEditRoom?: () => void;
  /** True when the scene's room has an active (non-completed) CombatEncounter (#2157). */
  hasActiveEncounter?: boolean;
  /** True when the scene's room has an active (unresolved) Battle (#2157). */
  hasActiveBattle?: boolean;
  onRefreshRoomState?: () => void;
  roomStateResyncStatus?: 'idle' | 'pending' | 'success' | 'partial' | 'failure';
  roomStateResyncError?: string;
}

export function RoomHeader({
  name,
  scene,
  onStartScene,
  onEndScene,
  isStartPending,
  isEndPending,
  canEdit = false,
  onEditRoom,
  hasActiveEncounter = false,
  hasActiveBattle = false,
  onRefreshRoomState,
  roomStateResyncStatus = 'idle',
  roomStateResyncError,
}: RoomHeaderProps) {
  return (
    <div className="border-b px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{name}</h3>
        <div className="flex items-center gap-1">
          {onRefreshRoomState && (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 px-2 text-xs text-foreground"
              onClick={onRefreshRoomState}
              disabled={roomStateResyncStatus === 'pending'}
              aria-label="Refresh room state"
            >
              {roomStateResyncStatus === 'pending' ? 'Refreshing…' : 'Refresh'}
            </Button>
          )}
          {canEdit && onEditRoom && (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 px-2 text-xs text-foreground"
              onClick={onEditRoom}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
      {roomStateResyncStatus !== 'idle' && (
        <p className="mt-1 text-xs text-muted-foreground" role="status" aria-live="polite">
          {roomStateResyncStatus === 'pending' && 'Refreshing room state…'}
          {roomStateResyncStatus === 'success' && 'Room state refreshed.'}
          {roomStateResyncStatus === 'partial' &&
            'Room state refreshed, but confirmation was lost.'}
          {roomStateResyncStatus === 'failure' &&
            (roomStateResyncError ?? 'Room state refresh failed. Retry.')}
        </p>
      )}
      {scene ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Link to={`/scenes/${scene.id}`}>
            <Badge variant="secondary" className="text-xs">
              Scene: {scene.name}
            </Badge>
          </Link>
          {hasActiveEncounter && (
            <Badge variant="destructive" className="text-xs" data-testid="room-header-combat-badge">
              In Combat
            </Badge>
          )}
          {hasActiveBattle && (
            <Link to={`/scenes/${scene.id}/battle`}>
              <Badge
                variant="destructive"
                className="text-xs"
                data-testid="room-header-battle-badge"
              >
                Battle
              </Badge>
            </Link>
          )}
          {scene.has_unseen_observer && (
            <Badge variant="destructive" className="text-xs">
              ⚠ OOC: an unseen observer is present
            </Badge>
          )}
          {scene.is_owner && (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 px-2 text-xs text-destructive"
              onClick={onEndScene}
              disabled={isEndPending}
            >
              End
            </Button>
          )}
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 min-h-11 px-2 text-xs text-foreground"
          onClick={onStartScene}
          disabled={isStartPending}
        >
          Start Scene
        </Button>
      )}
    </div>
  );
}
